import { Avatar } from './Avatar';
import { Icon } from './Icon';
import { givenName } from '../domain/names';
import { hebrewLabel, nextOccasionFor, occasionDayLabel, type HolidayKey, type Occasion, type OccasionKind } from '../domain/occasions';
import type { Lang, PersonWithNames, UUID } from '../domain/types';
import { useI18n, type MessageKey } from '../i18n';
import { useApp } from '../app/AppState';
import { Link } from '../app/router';

/** The icon that stands for each kind of day (anniversaries reuse the heart). */
export function occasionIcon(kind: OccasionKind): string {
  return kind === 'birthday' ? 'cake' : kind === 'anniversary' ? 'heart' : kind === 'yahrzeit' ? 'candle' : 'sparkle';
}

const HOLIDAY_KEY: Record<HolidayKey, MessageKey> = {
  roshHashana: 'holiday.roshHashana', yomKippur: 'holiday.yomKippur', sukkot: 'holiday.sukkot', simchatTorah: 'holiday.simchatTorah', chanukah: 'holiday.chanukah',
  tuBishvat: 'holiday.tuBishvat', purim: 'holiday.purim', pesach: 'holiday.pesach', yomHaatzmaut: 'holiday.yomHaatzmaut', lagBaomer: 'holiday.lagBaomer', shavuot: 'holiday.shavuot', tishaBav: 'holiday.tishaBav',
};

/** The greeting said on the day itself. */
const HOLIDAY_GREETING: Partial<Record<HolidayKey, MessageKey>> = {
  roshHashana: 'holiday.greeting.shanaTova', yomKippur: 'holiday.greeting.gmar', tishaBav: 'holiday.greeting.fast',
};

/** "Lior turns 14" / "Sarah & Tom: 16 years married" / "Saba Moshe's yahrzeit · 14 Cheshvan" / "Rosh Hashana" — without the day. */
export function occasionText(o: Occasion, lang: Lang, t: (k: MessageKey, p?: Record<string, string | number | undefined>) => string, peopleById: Map<UUID, PersonWithNames>, firstPersonId?: UUID): string {
  // On a person's own card their name comes first ("Sarah & Tom", not "Tom & Sarah").
  const ordered = firstPersonId && o.personIds.includes(firstPersonId) ? [firstPersonId, ...o.personIds.filter((id) => id !== firstPersonId)] : o.personIds;
  const names = ordered.map((id) => peopleById.get(id)).filter(Boolean).map((p) => givenName(p!, lang));
  switch (o.kind) {
    case 'birthday':
      return o.years !== undefined ? t('occasion.birthday', { name: names[0], n: o.years }) : t('occasion.birthdayNoAge', { name: names[0] });
    case 'anniversary': {
      const pair = names.join(lang === 'he' ? ' ו' : ' & ');
      return o.years !== undefined ? t('occasion.anniversary', { names: pair, n: o.years }) : t('occasion.anniversaryNoYears', { names: pair });
    }
    case 'yahrzeit':
      return t('occasion.yahrzeit', { name: names[0], hebrew: hebrewLabel(o, lang) });
    case 'holiday':
      return o.holiday ? t(HOLIDAY_KEY[o.holiday]) : '';
  }
}

function useDayLabel() {
  const { t, lang } = useI18n();
  return (o: Occasion) => occasionDayLabel(o, lang, { today: t('occasion.today'), tomorrow: t('occasion.tomorrow') });
}

/**
 * One quiet line on a person's card or profile: their soonest day in the
 * coming weeks. Nothing when there is none — the card stays as it was.
 */
export function OccasionLine({ personId, className = '' }: { personId: UUID; className?: string }) {
  const { t, lang } = useI18n();
  const { occasions, peopleById } = useApp();
  const dayOf = useDayLabel();
  const o = nextOccasionFor(occasions, personId);
  if (!o) return null;
  return (
    <div className={`occasion-line is-${o.kind} ${o.inDays === 0 ? 'is-today' : ''} ${className}`}>
      <Icon name={occasionIcon(o.kind)} />
      <span>
        {occasionText(o, lang, t, peopleById, personId)} · {dayOf(o)}
      </span>
    </div>
  );
}

/**
 * "Coming up" at the top of the bell: the family's next days, soonest first.
 * Derived, never stored — nothing to mark as read, nothing held by Shabbat mode.
 */
export function ComingUp({ limit = 6 }: { limit?: number }) {
  const { t, lang } = useI18n();
  const { occasions, peopleById, closePanel } = useApp();
  const dayOf = useDayLabel();
  if (occasions.length === 0) return null;
  // The family's own days first; the chagim fill in behind them (two at most, so a festival week does not crowd out a birthday).
  const personal = occasions.filter((o) => o.kind !== 'holiday');
  const holidays = occasions.filter((o) => o.kind === 'holiday').slice(0, 2);
  const rows = [...personal, ...holidays].sort((a, b) => a.inDays - b.inDays).slice(0, limit);
  return (
    <section className="coming-up" aria-label={t('occasion.comingUp')}>
      <h3 className="latest-title">{t('occasion.comingUp')}</h3>
      <ul className="list-reset stack-sm">
        {rows.map((o) => {
          const people = o.personIds.map((id) => peopleById.get(id)).filter(Boolean) as PersonWithNames[];
          const greeting = o.kind === 'holiday' && o.inDays === 0 && o.holiday ? t(HOLIDAY_GREETING[o.holiday] ?? 'holiday.greeting.chag') : null;
          const inner = (
            <>
              <span className={`occasion-mark is-${o.kind}`} aria-hidden="true">
                {people.length ? people.slice(0, 2).map((p) => <Avatar key={p.id} person={p} lang={lang} size="sm" />) : <Icon name={occasionIcon(o.kind)} />}
              </span>
              <span className="grow">
                <span className="bold">{occasionText(o, lang, t, peopleById)}</span>
                <span className="small muted block">
                  {dayOf(o)}
                  {o.kind === 'holiday' && o.hebrew ? ` · ${hebrewLabel(o, lang)}` : ''}
                  {greeting ? ` · ${greeting}` : ''}
                </span>
              </span>
              {people.length > 0 && <Icon name={occasionIcon(o.kind)} className={`occasion-kind is-${o.kind}`} />}
            </>
          );
          return (
            <li key={o.id}>
              {people.length ? (
                <Link to={`/people/${people[0].id}`} className={`occasion-row card-soft ${o.inDays === 0 ? 'is-today' : ''}`} onClick={() => closePanel()}>
                  {inner}
                </Link>
              ) : (
                <div className={`occasion-row card-soft is-holiday ${o.inDays === 0 ? 'is-today' : ''}`}>{inner}</div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
